import { useCallback } from 'react';
import {
  SAMPLES_ROUTE_PATH,
  useCreateSample,
  useDeleteSample,
  useUpdateSample,
  type SampleFilters,
  type SampleResponse,
  type SampleType,
} from '@/entities/sample';
import { useAppNavigate } from '@/shared/lib';

/** Что сохраняется в новую выборку: отбор — у выборки по фильтрам, состав — у выборки по записям */
type CreateParams = {
  name: string;
  /** Текущий отбор — уходит только у выборки по фильтрам */
  filters: SampleFilters;
  /** Набранные диалоги — уходят только у выборки по набору записей */
  conversationIds: number[];
};

/**
 * Действия над выборками раздела: открыть, создать из текущего состояния, записать в открытую
 * выборку её отбор или состав, переименовать и удалить.
 *
 * Тип выборки задаётся при создании и дальше не меняется, поэтому в теле обновления уходит поле
 * только своего рода: отбор — у выборки по фильтрам, состав — у выборки по набору записей.
 */
export const useSampleActions = () => {
  const navigate = useAppNavigate();
  const { mutate: create, isPending: isCreating } = useCreateSample();
  const { mutate: update, isPending: isUpdating } = useUpdateSample();
  const { mutate: remove } = useDeleteSample();

  /** Переход к выборке; `null` — список без ограничения выборкой */
  const openSample = useCallback(
    (sampleId: number | null) => {
      navigate(SAMPLES_ROUTE_PATH, { sampleId: sampleId == null ? null : String(sampleId), id: null });
    },
    [navigate],
  );

  // Тип новой выборки определяет набранное, а не выбор пользователя: отмечена хотя бы одна запись —
  // выборка запомнит их состав, ни одной — действующий отбор.
  const createSample = useCallback(
    ({ name, filters, conversationIds }: CreateParams) => {
      const type: SampleType = conversationIds.length ? 'records' : 'filters';
      create(
        {
          name,
          type,
          ...(type === 'filters' ? { filters } : { conversationIds }),
        },
        { onSuccess: ({ id }) => openSample(id) },
      );
    },
    [create, openSample],
  );

  /** Записывает в открытую выборку её отбор либо состав — смотря какого она рода */
  const saveSample = useCallback(
    (
      sample: SampleResponse,
      next: { filters: SampleFilters; conversationIds: number[] },
      options?: { onSuccess?: () => void },
    ) => {
      update(
        {
          id: sample.id,
          payload: {
            name: sample.name,
            description: sample.description,
            ...(sample.type === 'filters' ? { filters: next.filters } : { conversationIds: next.conversationIds }),
          },
        },
        { onSuccess: () => options?.onSuccess?.() },
      );
    },
    [update],
  );

  // Название меняется отдельно от отбора и состава: они в тело не идут, и выборка остаётся как была.
  const renameSample = useCallback(
    (sample: { id: number; name: string; description?: string }, name: string) => {
      update({ id: sample.id, payload: { name, description: sample.description } });
    },
    [update],
  );

  /** Удаляет выборку; открытую — с возвратом к списку без ограничения выборкой */
  const deleteSample = useCallback(
    (sampleId: number, options?: { isOpened?: boolean }) => {
      remove(sampleId, {
        onSuccess: () => {
          if (options?.isOpened) openSample(null);
        },
      });
    },
    [remove, openSample],
  );

  return { openSample, createSample, saveSample, renameSample, deleteSample, isSaving: isCreating || isUpdating };
};
